// src/pages/Gallery/GalleryPage.jsx
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import "./GalleryPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function GalleryPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Fetch gallery events from backend
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/gallery`);
        setEvents(res.data);
      } catch (err) {
        console.error("Gallery Fetch Error:", err);
        setError("Failed to load gallery events.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Open lightbox modal
  const openLightbox = (photos, index) => {
    setLightboxImages(photos);
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  // Navigate prev
  const prevPhoto = () => {
    setPhotoIndex(
      (photoIndex + lightboxImages.length - 1) % lightboxImages.length
    );
  };

  // Navigate next
  const nextPhoto = () => {
    setPhotoIndex((photoIndex + 1) % lightboxImages.length);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setPhotoIndex(0);
  };

  return (
    <section className="gallery-page">
      <div className="container">
        {/* Page Header */}
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>Gallery</h1>
          <p>Explore alumni albums with images from events and workshops.</p>
        </motion.div>

        {loading && <p>Loading gallery...</p>}
        {error && <p className="error-msg">{error}</p>}

        {!loading &&
          events.map((event) => {
            const photos = event.photos || [];
            const previewPhotos = photos.slice(0, 3);
            const extraCount = photos.length - previewPhotos.length;

            return (
              <div key={event._id} className="album-section">
                <h2 className="album-title">{event.eventTitle}</h2>
                <div className="gallery-grid">
                  {previewPhotos.map((photo, idx) => (
                    <motion.div
                      key={photo._id}
                      className="gallery-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ scale: 1.03 }}
                      onClick={() =>
                        openLightbox(photos.map((p) => p.imageUrl), idx)
                      }
                    >
                      <img
                        src={photo.imageUrl}
                        alt={`Photo ${idx + 1}`}
                        className="gallery-image"
                      />
                    </motion.div>
                  ))}

                  {extraCount > 0 && (
                    <div
                      className="gallery-card more-overlay"
                      onClick={() =>
                        openLightbox(photos.map((p) => p.imageUrl), 3)
                      }
                    >
                      +{extraCount}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        {/* Custom Lightbox */}
        {lightboxOpen && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
            >
              <button className="lightbox-close" onClick={closeLightbox}>
                &times;
              </button>
              <button className="lightbox-prev" onClick={prevPhoto}>
                &#10094;
              </button>
              <img
                src={lightboxImages[photoIndex]}
                alt={`Lightbox ${photoIndex + 1}`}
                className="lightbox-image"
              />
              <button className="lightbox-next" onClick={nextPhoto}>
                &#10095;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}